import {
  CommunicationService,
  bindAdvancementHotkeys,
  isDesktop,
} from '@carboncopyco/mimesis-shared';
import type { TourFrame, TourStep } from '@carboncopyco/types';
import { useEffect, useMemo } from 'react';
import type { UseIncrementalNavigationReturnType } from './useIncrementalNavigation';

interface IUseReplayerListeners {
  playableFrames: TourFrame[];
  playableSteps: TourStep[];
  activeFrame: TourFrame;
  activeStep: TourStep;
  lastPlayedStep: TourStep;
  tabId?: number;
  activeTabId?: number;
  onPostMessageHandler: (payload: any) => void;
  refreshTooltip: () => void;
  incrementalStepNavigation: UseIncrementalNavigationReturnType;
}

export const useReplayerListeners = ({
  playableFrames,
  playableSteps,
  activeFrame,
  activeStep,
  lastPlayedStep,
  activeTabId,
  tabId,
  onPostMessageHandler,
  refreshTooltip,
  incrementalStepNavigation,
}: IUseReplayerListeners) => {
  const communicationService = new CommunicationService({
    ignoreDeviceContext: true,
  });

  const isCurrentTabActive = useMemo(() => {
    return activeTabId === tabId;
  }, [activeTabId, tabId]);

  useEffect(() => {
    // for web, it should always initialise communication
    // for desktop only when tab is focused
    if (isCurrentTabActive) {
      communicationService.onMessage(onPostMessageHandler);
    }
    return () => {
      communicationService.removeMessageListener(onPostMessageHandler);
    };
  }, [
    communicationService,
    isCurrentTabActive,
    playableFrames,
    activeFrame,
    activeStep,
    lastPlayedStep,
  ]);

  useEffect(() => {
    if (isDesktop) {
      refreshTooltip();
    }
  }, [isCurrentTabActive]);

  useEffect(() => {
    const activeStepIndex = playableSteps.findIndex(
      (playableStep) => playableStep?.stepId === activeStep?.stepId
    );

    return bindAdvancementHotkeys({
      target: window,
      onNext: () => {
        const isLastStep = activeStepIndex === playableSteps.length - 1;
        if (!isCurrentTabActive || isLastStep) {
          return;
        }
        incrementalStepNavigation('next', true);
      },
      onPrevious: () => {
        const isFirstStep = activeStepIndex === 0;
        if (!isCurrentTabActive || isFirstStep) {
          return;
        }
        incrementalStepNavigation('back', true);
      },
    });
  }, [
    isCurrentTabActive,
    playableFrames,
    playableSteps,
    activeFrame,
    activeStep,
    lastPlayedStep,
  ]);
};
